// GloBird ZeroHero plan settings shared by registration, onboarding, site
// edit, and the battery plan chart.
//
// The ZeroCharge free-import window is a per-site setting stored in the
// site's price_provider_credentials as free_window_start/free_window_end
// ("HH:MM", site-local). GloBird has moved the window before (11am-2pm until
// mid-2026, 12pm-3pm since), so it's selectable rather than hard-coded —
// mirror of shared/utils/globird_window.py on the backend.

export interface GlobirdFreeWindowOption {
  start: string; // "HH:MM"
  end: string; // "HH:MM"
  label: string;
}

export const GLOBIRD_FREE_WINDOW_OPTIONS: GlobirdFreeWindowOption[] = [
  { start: '12:00', end: '15:00', label: '12pm – 3pm (current plan)' },
  { start: '11:00', end: '14:00', label: '11am – 2pm (plan before July 2026)' },
];

// Default = the plan's current window. Keep in sync with
// DEFAULT_FREE_WINDOW_START_MIN/END_MIN in shared/utils/globird_window.py.
export const GLOBIRD_DEFAULT_FREE_WINDOW = GLOBIRD_FREE_WINDOW_OPTIONS[0];

function parseHHMM(value: unknown): number | null {
  if (typeof value !== 'string') return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const minutes = Number(m[1]) * 60 + Number(m[2]);
  return Number(m[2]) < 60 && minutes >= 0 && minutes < 24 * 60 ? minutes : null;
}

/**
 * Resolve a site's free window from its price provider credentials, falling
 * back to the plan default when unset/invalid (same rule as the backend).
 */
export function resolveFreeWindow(
  credentials: Record<string, unknown> | null | undefined,
): { start: string; end: string; startHour: number; endHour: number } {
  const startMin = parseHHMM(credentials?.free_window_start);
  const endMin = parseHHMM(credentials?.free_window_end);
  if (startMin === null || endMin === null || startMin === endMin) {
    const d = GLOBIRD_DEFAULT_FREE_WINDOW;
    return {
      start: d.start,
      end: d.end,
      startHour: parseHHMM(d.start)! / 60,
      endHour: parseHHMM(d.end)! / 60,
    };
  }
  return {
    start: credentials!.free_window_start as string,
    end: credentials!.free_window_end as string,
    startHour: startMin / 60,
    endHour: endMin / 60,
  };
}

/** "12:00"/"15:00" -> "12pm–3pm" for display copy. */
export function formatFreeWindow(start: string, end: string): string {
  const fmt = (hhmm: string): string => {
    const min = parseHHMM(hhmm);
    if (min === null) return hhmm;
    const h = Math.floor(min / 60);
    const m = min % 60;
    const suffix = h < 12 ? 'am' : 'pm';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return m === 0 ? `${h12}${suffix}` : `${h12}:${String(m).padStart(2, '0')}${suffix}`;
  };
  return `${fmt(start)}–${fmt(end)}`;
}
